import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, isDevMode } from '@angular/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';

import { provideAnimations } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { Theme } from './app/core/models/theme.model';
import { ThemeService } from './app/core/services/theme.service';
import { environment } from './environments/environment';
import { TranslocoHttpLoader } from './transloco-loader';

import { provideSweetAlert2 } from '@sweetalert2/ngx-sweetalert2';
import { LucideAngularModule } from 'lucide-angular';
import { KeyStorage } from './app/core/constants/key-storage.stor';
import { ApiInterceptorInterceptor } from './app/core/interceptors/api-interceptor/api-interceptor.interceptor';
import { AppStorageService } from './app/core/services/store/app-storage.service';
import { TranslocoTitleStrategy } from './app/core/services/strategies/transloco-title.strategy';
import { TitleStrategy } from '@angular/router';

if (environment.production) {
  enableProdMode();
  //show this warning only on prod mode
  if (window) {
    selfXSSWarning();
  }
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(BrowserModule, AppRoutingModule, LucideAngularModule),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([ApiInterceptorInterceptor])),
    // ? { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    { provide: TitleStrategy, useClass: TranslocoTitleStrategy },
    provideTransloco({
      config: {
        fallbackLang: 'en',
        availableLangs: ['en', 'ar'],
        defaultLang: 'en',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideSweetAlert2({
      fireOnInit: false,
      dismissOnDestroy: true,
    }),
  ],
})
  .then((ref) => {
    const transloco = ref.injector.get(TranslocoService);
    const themeService = ref.injector.get(ThemeService);
    const storage = ref.injector.get(AppStorageService);

    let themeSaved: Theme | null = null;
    // const savedTheme = localStorage.getItem(KeyStorage.keyTheme);
    // const savedTheme = storage.getItem<string>(KeyStorage.keyTheme);
    const savedTheme = storage.getItem<Theme>(KeyStorage.keyTheme);

    if (savedTheme) {
      try {
        // themeSaved = JSON.parse(savedTheme) as Theme;
        themeSaved = savedTheme;
        console.log('Loaded theme:', themeSaved);

        themeService.theme.update((theme) => ({
          ...theme,
          language: themeSaved?.language || theme.language,
        }));

        if (themeSaved?.language) {
          transloco.setActiveLang(themeSaved.language);
          document.documentElement.setAttribute(
            'dir',
            themeSaved.language === 'ar' ? 'rtl' : 'ltr',
          );
        }
      } catch (e) {
        console.error('Error parsing theme from localStorage:', e);
      }
    }

    if (!themeSaved || !themeSaved.language) {
      const browserLang = navigator.language.split('-')[0].toLowerCase();
      const langToUse = ['ar', 'en'].includes(browserLang) ? browserLang : 'ar';

      transloco.setActiveLang(langToUse);

      themeService.theme.update((theme) => ({
        ...theme,
        language: langToUse,
      }));

      document.documentElement.setAttribute('dir', langToUse === 'ar' ? 'rtl' : 'ltr');
    }
    console.log('Application started');
  })
  .catch((err) => console.error(err));

function selfXSSWarning() {
  setTimeout(() => {
    console.log(
      '%c** STOP **',
      'font-weight:bold; font: 2.5em Arial; color: white; background-color: #e11d48; padding-left: 15px; padding-right: 15px; border-radius: 25px; padding-top: 5px; padding-bottom: 5px;',
    );
    console.log(
      `\n%cThis is a browser feature intended for developers. Using this console may allow attackers to impersonate you and steal your information sing an attack called Self-XSS. Do not enter or paste code that you do not understand.`,
      'font-weight:bold; font: 2em Arial; color: #e11d48;',
    );
  });
}
